import { apiClient } from '../client';

export interface Role {
  _id: string;
  name: string;
  display_name: string;
  description: string;
  permissions: string[];
  is_system_role: boolean;
  scope: 'global' | 'campus';
  created_at: Date;
  updated_at: Date;
}

export interface Permission {
  _id: string;
  resource: string;
  action: string;
  code: string;
  description: string;
  created_at: Date;
  updated_at: Date;
}

export interface GroupedPermissions {
  [resource: string]: Array<{
    action: string;
    code: string;
    description: string;
  }>;
}

export interface UserRole {
  _id: string;
  first_name: string;
  last_name: string;
  email: string;
  primary_role: string;
  roles: Array<{
    role: string;
    campus_id?: {
      _id: string;
      campus_name: string;
    };
    assigned_by: {
      _id: string;
      first_name: string;
      last_name: string;
      email: string;
    };
    assigned_at: Date;
  }>;
}

export interface AssignRoleRequest {
  role: string;
  campus_id?: string;
}

export interface RemoveRoleRequest {
  role: string;
  campus_id?: string;
}

export interface CheckPermissionRequest {
  permission: string;
  campus_id?: string;
}

export interface CheckPermissionResponse {
  success: boolean;
  has_permission: boolean;
  granted_by: string | null;
  permission: string;
  campus_id: string | null;
}

export interface CreateRoleRequest {
  name: string;
  display_name: string;
  description: string;
  permissions: string[];
  scope: 'global' | 'campus';
}

export interface UpdateRoleRequest {
  display_name?: string;
  description?: string;
  permissions?: string[];
  scope?: 'global' | 'campus';
}

export interface CreatePermissionRequest {
  resource: string;
  action: string;
  description: string;
}

export const rolesAPI = {
  // Get all available roles
  getRoles: async (): Promise<{ success: boolean; roles: Role[] }> => {
    const response = await apiClient.get('/api/admin/roles');
    return response.data;
  },

  // Get all available permissions
  getPermissions: async (): Promise<{ success: boolean; permissions: GroupedPermissions }> => {
    const response = await apiClient.get('/api/admin/permissions');
    return response.data;
  },

  // Get user's roles
  getUserRoles: async (userId: string): Promise<{ success: boolean; user: UserRole }> => {
    const response = await apiClient.get(`/api/admin/users/${userId}/roles`);
    return response.data;
  },

  // Assign role to user
  assignRole: async (userId: string, data: AssignRoleRequest): Promise<{ success: boolean; message: string; user: UserRole }> => {
    const response = await apiClient.post(`/api/admin/users/${userId}/assign-role`, data);
    return response.data;
  },

  // Remove role from user
  removeRole: async (userId: string, data: RemoveRoleRequest): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.delete(`/api/admin/users/${userId}/remove-role`, { data });
    return response.data;
  },

  // Check if user has permission
  checkPermission: async (userId: string, data: CheckPermissionRequest): Promise<CheckPermissionResponse> => {
    const response = await apiClient.post(`/api/admin/users/${userId}/check-permission`, data);
    return response.data;
  },

  // Create new role
  createRole: async (data: CreateRoleRequest): Promise<{ success: boolean; role: Role; message: string }> => {
    const response = await apiClient.post('/api/admin/roles', data);
    return response.data;
  },

  // Update existing role
  updateRole: async (roleId: string, data: UpdateRoleRequest): Promise<{ success: boolean; role: Role; message: string }> => {
    const response = await apiClient.put(`/api/admin/roles/${roleId}`, data);
    return response.data;
  },

  // Delete role
  deleteRole: async (roleId: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.delete(`/api/admin/roles/${roleId}`);
    return response.data;
  },

  // Create new permission
  createPermission: async (data: CreatePermissionRequest): Promise<{ success: boolean; permission: Permission; message: string }> => {
    const response = await apiClient.post('/api/admin/permissions', data);
    return response.data;
  },

  // Delete permission
  deletePermission: async (permissionId: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.delete(`/api/admin/permissions/${permissionId}`);
    return response.data;
  },
};
